import * as React from 'react';
import { useColorScheme } from '@mui/material/styles';
import { useGlobalContext } from '../contexts/GlobalContext';
import { useFeatureFlag, FEATURE_FLAGS } from '../contexts/FeatureFlagContext';

/**
 * Component that always syncs MUI's color scheme with GlobalContext.
 * This ensures the theme is properly set even when the toggle is hidden.
 */
export default function ThemeSync() {
  const { mode, setMode } = useColorScheme();
  const { themeMode, setThemeMode, isLoading } = useGlobalContext();
  const { enabled: themeToggleEnabled, isLoading: featureFlagsLoading } =
    useFeatureFlag(FEATURE_FLAGS.ENABLE_THEME_TOGGLE);

  // Determine the effective theme mode
  // If feature flag is disabled, always use 'light'
  // If enabled, use the themeMode from GlobalContext (but convert 'system' to actual system preference)
  const effectiveMode = React.useMemo(() => {
    // Wait for contexts to be ready
    if (isLoading || featureFlagsLoading) {
      return null;
    }

    // If feature flag is disabled, always force 'light'
    if (!themeToggleEnabled) {
      return 'light';
    }

    // Feature flag is enabled - use the themeMode from GlobalContext
    // If themeMode is 'system', detect the actual system preference
    if (themeMode === 'system') {
      // Check system preference
      if (
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      ) {
        return 'dark';
      }
      return 'light';
    }

    // Use the explicitly set theme mode (light or dark)
    return themeMode || 'light';
  }, [themeMode, themeToggleEnabled, isLoading, featureFlagsLoading]);

  // If feature flag is disabled, immediately force 'light' mode
  // This runs even before GlobalContext finishes loading to prevent dark mode flash
  React.useEffect(() => {
    if (!mode) {
      return; // MUI color scheme not ready yet
    }

    // If feature flags are loaded and toggle is disabled, force light immediately
    if (!featureFlagsLoading && !themeToggleEnabled && mode !== 'light') {
      setMode('light');
    }
  }, [mode, setMode, featureFlagsLoading, themeToggleEnabled]);

  // Sync MUI color scheme with effective mode
  // This runs when both contexts are ready and we have an effective mode
  React.useEffect(() => {
    if (!mode || !effectiveMode) {
      return; // Not ready yet
    }

    if (effectiveMode !== mode) {
      setMode(effectiveMode);
    }
  }, [effectiveMode, mode, setMode]);

  // If feature flag is disabled and themeMode is not 'light', update GlobalContext
  React.useEffect(() => {
    if (
      !isLoading &&
      !featureFlagsLoading &&
      !themeToggleEnabled &&
      themeMode !== 'light'
    ) {
      setThemeMode('light');
    }
  }, [
    themeToggleEnabled,
    themeMode,
    setThemeMode,
    isLoading,
    featureFlagsLoading,
  ]);

  return null;
}
